import {
  useEffect,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent,
} from "react";

import { FullscreenIcon, IconButton, SidePeek } from "../../components/ui";
import type { XLMModel } from "../api/e2e";
import type { ModelReference } from "../lib/modelReferences";
import AgentModelChat from "./AgentModelChat";

export const DEFAULT_MODEL_CHAT_WIDTH = 400;
export const MIN_MODEL_CHAT_WIDTH = 320;
export const MAX_MODEL_CHAT_WIDTH = 720;

const MODEL_CHAT_VIEWPORT_GUTTER = 80;
const MODEL_CHAT_KEYBOARD_RESIZE_STEP = 24;

function maximumModelChatWidth(viewportWidth: number): number {
  return Math.max(
    MIN_MODEL_CHAT_WIDTH,
    Math.min(MAX_MODEL_CHAT_WIDTH, viewportWidth - MODEL_CHAT_VIEWPORT_GUTTER),
  );
}

export function clampModelChatWidth(
  width: number,
  viewportWidth: number,
): number {
  return Math.min(
    maximumModelChatWidth(viewportWidth),
    Math.max(MIN_MODEL_CHAT_WIDTH, Math.round(width)),
  );
}

export default function AgentModelChatSidePeek({
  companyId,
  versionId,
  isRegistered,
  model,
  modelReferenceState,
  revisionLocked = false,
  width,
  fullScreen,
  onClose,
  onResize,
  onFullScreenChange,
  onEditActivityChange,
  onOpenModelReference,
}: {
  companyId: string;
  versionId: string;
  isRegistered: boolean;
  model: XLMModel | undefined;
  modelReferenceState: "loading" | "ready" | "error";
  revisionLocked?: boolean;
  width: number;
  fullScreen: boolean;
  onClose: () => void;
  onResize: (width: number) => void;
  onFullScreenChange: (fullScreen: boolean) => void;
  onEditActivityChange: (active: boolean) => void;
  onOpenModelReference: (reference: ModelReference) => void;
}) {
  const [resizing, setResizing] = useState(false);
  const maximumWidth = maximumModelChatWidth(window.innerWidth);

  useEffect(() => {
    if (!fullScreen) return;
    const exitFullScreen = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onFullScreenChange(false);
    };
    window.addEventListener("keydown", exitFullScreen);
    return () => window.removeEventListener("keydown", exitFullScreen);
  }, [fullScreen, onFullScreenChange]);

  useEffect(() => {
    if (fullScreen) return;
    const fitToViewport = () => {
      const nextWidth = clampModelChatWidth(width, window.innerWidth);
      if (nextWidth !== width) onResize(nextWidth);
    };
    window.addEventListener("resize", fitToViewport);
    fitToViewport();
    return () => window.removeEventListener("resize", fitToViewport);
  }, [fullScreen, onResize, width]);

  const resizeFromPointer = (clientX: number) => {
    onResize(
      clampModelChatWidth(window.innerWidth - clientX, window.innerWidth),
    );
  };

  const startResize = (event: PointerEvent<HTMLDivElement>) => {
    if (fullScreen) return;
    event.preventDefault();
    setResizing(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    resizeFromPointer(event.clientX);
  };

  const stopResize = (event: PointerEvent<HTMLDivElement>) => {
    if (!resizing) return;
    setResizing(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const resizeWithKeyboard = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    let nextWidth: number | null = null;
    if (event.key === "ArrowLeft") {
      nextWidth = width + MODEL_CHAT_KEYBOARD_RESIZE_STEP;
    } else if (event.key === "ArrowRight") {
      nextWidth = width - MODEL_CHAT_KEYBOARD_RESIZE_STEP;
    } else if (event.key === "Home") {
      nextWidth = MIN_MODEL_CHAT_WIDTH;
    } else if (event.key === "End") {
      nextWidth = maximumWidth;
    }
    if (nextWidth === null) return;
    event.preventDefault();
    onResize(clampModelChatWidth(nextWidth, window.innerWidth));
  };

  const fullScreenLabel = fullScreen
    ? "Exit full screen model chat"
    : "Show model chat full screen";

  return (
    <SidePeek
      id="agent-model-chat-sidepeek"
      className={[
        "agent-model-chat-sidepeek",
        fullScreen ? "is-fullscreen" : "",
        resizing ? "is-resizing" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      ariaLabel="Model chat side peek"
      title="Ask model"
      closeLabel="Close model chat"
      headerless
      onClose={onClose}
    >
      {!fullScreen && (
        <div
          className="agent-model-chat-sidepeek__resize-handle"
          role="separator"
          aria-label="Resize model chat"
          aria-orientation="vertical"
          aria-valuemin={MIN_MODEL_CHAT_WIDTH}
          aria-valuemax={maximumWidth}
          aria-valuenow={width}
          tabIndex={0}
          onKeyDown={resizeWithKeyboard}
          onPointerDown={startResize}
          onPointerMove={(event) => {
            if (resizing) resizeFromPointer(event.clientX);
          }}
          onPointerUp={stopResize}
          onPointerCancel={stopResize}
          onLostPointerCapture={() => setResizing(false)}
        />
      )}
      <IconButton
        type="button"
        containerClassName="agent-model-chat-sidepeek__fullscreen"
        tone="quiet"
        size="sm"
        label={fullScreenLabel}
        tooltip={fullScreenLabel}
        icon={<FullscreenIcon active={fullScreen} />}
        aria-pressed={fullScreen}
        onClick={() => onFullScreenChange(!fullScreen)}
      />
      <AgentModelChat
        companyId={companyId}
        versionId={versionId}
        isRegistered={isRegistered}
        model={model}
        modelReferenceState={modelReferenceState}
        revisionLocked={revisionLocked}
        fullScreen={fullScreen}
        onEditActivityChange={onEditActivityChange}
        onOpenModelReference={onOpenModelReference}
      />
    </SidePeek>
  );
}
